import { parseGuestSavedLooks, serializeGuestSavedLooks } from '~/utils/guestSavedLooks'

/**
 * The logged-out visitor's saved outfits, kept in localStorage so they survive a
 * reload and a return visit, and merged into the account on sign-in.
 *
 * A near-verbatim twin of `useGuestWishlist`, and deliberately so: the two
 * lists have the same lifecycle (read after mount, never during setup or SSR,
 * never a cookie), and the one difference — a look id instead of a variant +
 * product pair — is not worth a shared abstraction that would have to explain
 * both shapes.
 */

const STORAGE_KEY = 'guest-saved-looks'

export function useGuestSavedLooks() {
  // Shared reactive copy. Starts empty on the server and stays empty through
  // hydration, so a cached document carries nobody's saved looks.
  const entries = useState<string[]>(STORAGE_KEY, () => [])
  const loaded = useState<boolean>(`${STORAGE_KEY}:loaded`, () => false)

  /** `null` means storage could not be read at all, which is not the same as empty. */
  function readStorage(): string[] | null {
    if (!import.meta.client)
      return null
    try {
      return parseGuestSavedLooks(window.localStorage.getItem(STORAGE_KEY))
    }
    catch {
      return null
    }
  }

  /** Applies a change against what is in storage right now, so two tabs do not clobber each other. */
  function mutate(apply: (current: string[]) => string[]): void {
    const next = apply(readStorage() ?? entries.value)
    entries.value = next
    if (!import.meta.client)
      return
    try {
      window.localStorage.setItem(STORAGE_KEY, serializeGuestSavedLooks(next))
    }
    catch {
      // Storage unavailable — the in-memory list still serves this visit.
    }
  }

  /** Called once from a client plugin after mount, never during setup (hydration would mismatch). */
  function load(): void {
    entries.value = readStorage() ?? []
    loaded.value = true
  }

  function syncFromStorage(): void {
    const stored = readStorage()
    if (stored)
      entries.value = stored
  }

  function has(lookId: string): boolean {
    return entries.value.includes(String(lookId))
  }

  function add(lookId: string): void {
    const id = String(lookId)
    mutate(current => (current.includes(id) ? current : [...current, id]))
  }

  function remove(lookId: string): void {
    const id = String(lookId)
    mutate(current => current.filter(entry => entry !== id))
  }

  /**
   * Drops ids the catalogue no longer resolves — a disabled or emptied outfit —
   * so the wishlist page and the header badge cannot disagree about the count.
   */
  function keepOnly(lookIds: Iterable<string>): void {
    const keep = new Set<string>()
    for (const id of lookIds)
      keep.add(String(id))

    if (entries.value.every(id => keep.has(id)))
      return

    mutate(current => current.filter(id => keep.has(id)))
  }

  function clear(): void {
    mutate(() => [])
  }

  return { entries, loaded, load, syncFromStorage, has, add, remove, keepOnly, clear }
}
